import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const SendCampaignModal = ({ isOpen, onClose, selectedLeads = [], leads = [] }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    type: 'email'
  });

  const campaignTypeOptions = [
    { value: 'email', label: 'Email Campaign' },
    { value: 'sms', label: 'SMS Campaign' },
    { value: 'social', label: 'Social Media' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.name?.trim()) {
      setError('Campaign name is required');
      return;
    }
    if (!user?.id) {
      setError('You must be logged in to create campaigns');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const recipientCount = selectedLeads?.length || leads?.length || 0;
      const { data, error: insertError } = await supabase
        ?.from('communication_campaigns')
        ?.insert([{
          user_id: user?.id,
          name: formData?.name,
          subject: formData?.subject || formData?.name,
          content: formData?.content || '',
          type: formData?.type || 'email',
          status: 'draft',
          recipient_count: recipientCount,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }])
        ?.select()
        ?.single();

      if (insertError) {
        setError(insertError?.message || 'Failed to create campaign');
        return;
      }

      setSuccess('Campaign created successfully!');
      setTimeout(() => {
        setSuccess('');
        onClose();
        navigate('/communication-hub');
      }, 1500);
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-elevation-3 w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Send" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Create Campaign</h2>
              <p className="text-sm text-muted-foreground">
                {selectedLeads?.length > 0 ? `Sending to ${selectedLeads?.length} selected lead(s)` : 'Create a new outreach campaign'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-red-500 shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-green-500 shrink-0" />
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          )}

          <Input
            label="Campaign Name *"
            placeholder="Q4 Outreach Campaign"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
          />

          <Input
            label="Subject Line"
            placeholder="Exclusive offer for you"
            value={formData?.subject}
            onChange={(e) => handleChange('subject', e?.target?.value)}
          />

          <Select
            label="Campaign Type"
            options={campaignTypeOptions}
            value={formData?.type}
            onChange={(value) => handleChange('type', value)}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Message Content</label>
            <textarea
              placeholder="Write your campaign message here..."
              value={formData?.content}
              onChange={(e) => handleChange('content', e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="default" disabled={loading} iconName={loading ? undefined : 'Send'} iconPosition="left">
              {loading ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendCampaignModal;
